import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { DEFAULT_CONTENT } from "@/lib/content";

const FIELDS = [
  { key: "hero_tagline", label: "Home — scritta sotto il logo" },
  { key: "manifesto_1", label: "Manifesto — paragrafo 1", textarea: true },
  { key: "manifesto_2", label: "Manifesto — paragrafo 2", textarea: true },
  { key: "manifesto_3", label: "Manifesto — paragrafo 3", textarea: true },
  { key: "manifesto_4", label: "Manifesto — paragrafo 4", textarea: true },
  { key: "sala_pesi_subtitle", label: "Sala Pesi — descrizione", textarea: true },
  { key: "sala_posing_subtitle", label: "Sala Posing — descrizione", textarea: true },
  { key: "zona_relax_subtitle", label: "Zona Relax — descrizione", textarea: true },
  { key: "brand_subtitle", label: "I Nostri Brand — descrizione", textarea: true },
  { key: "abbonamenti_desc", label: "Abbonamenti — descrizione", textarea: true },
  { key: "coaching_desc", label: "Coaching One to One — descrizione", textarea: true },
  { key: "giftcard_desc", label: "Gift Card — descrizione", textarea: true },
  { key: "logbook_desc", label: "Logbook — descrizione", textarea: true },
  { key: "logbook_url", label: "Logbook — link" },
  { key: "instagram_url", label: "Instagram — link profilo" },
  { key: "map_lat", label: "Mappa — latitudine" },
  { key: "map_lng", label: "Mappa — longitudine" },
  { key: "contact_phone", label: "Contatti — telefono" },
  { key: "contact_email", label: "Contatti — email" },
  { key: "contact_address", label: "Contatti — indirizzo" },
];

const inputClass =
  "w-full bg-ink border border-[rgba(176,132,51,0.3)] px-4 py-3 text-bone text-sm focus:outline-none focus:border-gold transition-colors";

export default function ContentEditor({ content, onSaved }) {
  const [values, setValues] = useState({ ...DEFAULT_CONTENT, ...(content || {}) });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setValues({ ...DEFAULT_CONTENT, ...(content || {}) });
  }, [content]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/content", values);
      toast.success("Contenuti salvati");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="admin-content-editor" className="border border-[rgba(176,132,51,0.25)] p-6 mb-10">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
        <h2 className="font-display text-xl tracking-[0.2em] text-gold">TESTI, LINK E MAPPA</h2>
        <button
          data-testid="content-save-button"
          onClick={save}
          disabled={busy}
          className="inline-flex items-center gap-2 bg-gold text-ink px-5 py-2 text-sm font-display tracking-[0.15em] hover:bg-gold-dark transition-colors disabled:opacity-50 self-start"
        >
          <Save size={16} /> {busy ? "SALVATAGGIO..." : "SALVA MODIFICHE"}
        </button>
      </div>
      <p className="text-bone/30 text-xs mb-6">
        Modifica i testi delle sezioni, i link e la posizione della mappa, poi premi "Salva modifiche". Per la
        mappa incolla latitudine e longitudine in formato decimale (es. 38.418500 e 15.902972).
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {FIELDS.map((f) => (
          <div key={f.key} className={f.textarea ? "md:col-span-2" : ""}>
            <label className="block text-bone/50 text-xs tracking-widest mb-2">{f.label.toUpperCase()}</label>
            {f.textarea ? (
              <textarea
                data-testid={`content-field-${f.key}`}
                rows={3}
                value={values[f.key] ?? ""}
                onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                className={inputClass}
              />
            ) : (
              <input
                data-testid={`content-field-${f.key}`}
                type="text"
                value={values[f.key] ?? ""}
                onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                className={inputClass}
              />
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
